import { Section, SectionContainer, SectionProps } from "@/components/Sections";
import { MappedLearningOpportunity, TeamMember } from "@/server/backend-api";
import { mcn } from "@/utils/mergeClassNames";
import { TeamMemberCard } from "../TeamMemberCard";
import { WorkspaceLinkCard } from "../WorkspaceLinkCard";
import {
  ColumnsSection,
  ColumnsSectionLeft,
  ColumnsSectionRight,
  ColumnsSectionSeperator,
} from "@/components/Sections/ColumnsSection";
import { LearningOpportunityInfoMessage } from "../LearningOpportunityInfoMessage";
import { AttendingSectionStatic } from "@/_staticContent/learningOppSections";
import dayjs from "dayjs";
import { useState } from "react";
import { IconActionArrowRight } from "@/components/Icons";

const attendingStatic = AttendingSectionStatic;

type Props = SectionProps & {
  learningOpp: MappedLearningOpportunity;
  teamchers: TeamMember[];
  learners: TeamMember[];
  showInitialHelpText: boolean;
  containerClassName?: string;
};

export const LearningOppAttendingSection = ({
  learningOpp,
  teamchers,
  learners,
  containerClassName,
  showInitialHelpText,
  ...sectionProps
}: Props) => {
  const { className, ...restSectionProps } = sectionProps;

  interface TeamProps {
    includeInitialHelpText: boolean;
    onViewAllLearnersClicked: () => void;
  }

  interface LmsProps {
    includeInitialHelpText: boolean;
    isLmsAccessible: boolean;
  }

  function InitialHelpText() {
    return (
      <LearningOpportunityInfoMessage heading="Welcome!">
        {AttendingSectionStatic.initialHelpText[learningOpp.type]}
      </LearningOpportunityInfoMessage>
    );
  }

  function TeamSection(props: TeamProps) {
    const hasTeachers = teamchers.length > 0;
    const MAX_LEARNERS = 6;
    const isLearnerListOverflowed = learners.length > MAX_LEARNERS;
    const selectedLearners = learners.slice(0, MAX_LEARNERS);
    const applyTeamMemberGradient = isLearnerListOverflowed;
    return (
      <ColumnsSectionLeft className={mcn(containerClassName, "")}>
        {showInitialHelpText && props.includeInitialHelpText && (
          <InitialHelpText />
        )}
        <h3 className="text-style-headline5 mt-xl ">
          {AttendingSectionStatic.teacherHeader[learningOpp.type]}
        </h3>
        <p className="text-style-body2 mb-md">
          {AttendingSectionStatic.teacherInfoText[learningOpp.type]}
        </p>

        <div
          className={mcn("grid grid-cols-12 tablet-lg:grid-cols-8 gap-default")}
        >
          {!hasTeachers && (
            <div className="col-span-full tablet-sm:col-span-6 tablet:col-span-4 bg-blue rounded-2 flex justify-center items-center min-h-[120px]">
              <h4 className="text-style-body2">TBA</h4>
            </div>
          )}
          {teamchers.map((item, index) => (
            <TeamMemberCard
              teamRole="Teacher"
              className="col-span-full tablet-sm:col-span-6 tablet:col-span-4"
              key={index}
              person={item}
              teacherLabel={
                AttendingSectionStatic.teacherTeamMemberLabel[learningOpp.type]
              }
            />
          ))}
        </div>

        <h3 className="text-style-headline5 mt-xl mb-xs">Learners</h3>
        <div
          className={mcn(
            "grid grid-cols-12 tablet-lg:grid-cols-8 gap-default !gap-y-xs"
          )}
        >
          {selectedLearners.map((item, index) => {
            // Need to apply gradient to last 1-3 learners depending on screen size.
            const isLastAndShouldApplyGradient =
              index === selectedLearners.length - 1 && applyTeamMemberGradient;
            const isSecondToLastAndShouldApplyGradient =
              index === selectedLearners.length - 2 && applyTeamMemberGradient;
            const isThirdToLastAndShouldApplyGradient =
              index === selectedLearners.length - 3 && applyTeamMemberGradient;
            return (
              <TeamMemberCard
                teamRole="Learner"
                className={mcn(
                  "col-span-full tablet-sm:col-span-6 tablet:col-span-4",
                  isLastAndShouldApplyGradient && "bg-team-member-gradient",
                  isSecondToLastAndShouldApplyGradient &&
                    "tablet-sm:bg-team-member-gradient",
                  isThirdToLastAndShouldApplyGradient &&
                    "tablet:max-tablet:bg-team-member-gradient"
                )}
                key={index}
                person={item}
              />
            );
          })}
          {isLearnerListOverflowed && (
            <div className="col-span-full text-light-yellow">
              <button
                className="underline"
                onClick={props.onViewAllLearnersClicked}
              >
                {`View all ${learners.length} learners`}
              </button>
            </div>
          )}
        </div>
      </ColumnsSectionLeft>
    );
  }

  function LmsSection(props: LmsProps) {
    const isLmsAccessibleKey = props.isLmsAccessible ? "active" : "disabled";
    return (
      <ColumnsSectionRight className={mcn(containerClassName)}>
        {showInitialHelpText && props.includeInitialHelpText && (
          <InitialHelpText />
        )}
        <h2 className="text-style-headline5 mt-xl">
          {attendingStatic.workspace.title}
        </h2>
        <p className="mb-md">
          {attendingStatic.workspace.text[learningOpp.type][isLmsAccessibleKey]}
        </p>
        <WorkspaceLinkCard
          url={learningOpp.lmsUrl}
          isEciuTeams={learningOpp.eciuLmsEnabled}
          disabled={isLmsAccessibleKey === "disabled"}
        />
      </ColumnsSectionRight>
    );
  }

  const [isFullTeamSectionOpen, setIsFullTeamSectionOpen] = useState(false);
  const hasLmsUrl = learningOpp.lmsUrl !== null;
  const accessDate =
    learningOpp.teamsAccessDate !== null
      ? dayjs(learningOpp.teamsAccessDate)
      : null;
  const now = dayjs();
  const isPastAccessDate =
    accessDate !== null &&
    (now.isSame(accessDate, "date") || now.isAfter(accessDate, "date"));
  const isLmsAccessible =
    hasLmsUrl && (accessDate === null || isPastAccessDate);

  if (isFullTeamSectionOpen) {
    return (
      <Section color="light-blue">
        <SectionContainer className="grid grid-cols-2 gap-x-default gap-y-2">
          <div className="col-span-full mb-6">
            <button
              className="flex items-center gap-x-2"
              onClick={() => setIsFullTeamSectionOpen(false)}
            >
              <IconActionArrowRight className="w-6 h-6 rotate-180" />
              Back
            </button>
          </div>
          <h2 className="text-style-headline5 col-span-full ml-2">
            All learners
          </h2>
          {learners.map((learner, index) => (
            <div key={index} className="col-span-full tablet-lg:col-span-1">
              <TeamMemberCard person={learner} teamRole={"Learner"} />
            </div>
          ))}
        </SectionContainer>
      </Section>
    );
  }

  return (
    <>
      <ColumnsSection
        {...restSectionProps}
        className={mcn(className, "hidden tablet-lg:block")}
      >
        <TeamSection
          includeInitialHelpText={true}
          onViewAllLearnersClicked={() => setIsFullTeamSectionOpen(true)}
        />
        <ColumnsSectionSeperator />
        <LmsSection
          includeInitialHelpText={false}
          isLmsAccessible={isLmsAccessible}
        />
      </ColumnsSection>
      <ColumnsSection
        {...restSectionProps}
        className={mcn(className, "tablet-lg:hidden")}
      >
        {hasLmsUrl && (
          <LmsSection
            includeInitialHelpText={hasLmsUrl}
            isLmsAccessible={isLmsAccessible}
          />
        )}
        <ColumnsSectionSeperator />
        <TeamSection
          includeInitialHelpText={!hasLmsUrl}
          onViewAllLearnersClicked={() => setIsFullTeamSectionOpen(true)}
        />
      </ColumnsSection>
    </>
  );
};
